import { useState } from 'react';
import { LOCALES, type InvitationDto, type Locale } from '@genoma/shared';

import { useI18n } from '../../i18n/index.tsx';
import {
  sendParticipantAccess,
  sendInvitation,
  updatePersonEmail,
  updatePersonLocale,
} from '../../services/dashboard.service.ts';
import { useCan } from '../../stores/auth.store.ts';

type Feedback = {
  email: string;
  emailIsPlaceholder: boolean;
  /** The language their questionnaire renders in. Null is the org default. */
  locale: Locale | null;
  invitations: InvitationDto[];
};

const LOCALE_LABELS: Record<Locale, string> = { es: 'Español', en: 'English' };

type Props = {
  /** The human, not the participant: raters are not participants. */
  personId: string;
  detail: Feedback;
  onChange: (changes: Partial<Feedback>) => void;
};

/**
 * Where this person's links go, and whether any have been sent.
 *
 * The 360 map arrived without email addresses, so every one of them gets typed
 * in by hand at some point and some of them get typed in wrong. A wrong address
 * is invisible from every other screen: the person simply never appears in the
 * responses, looking exactly like somebody who was asked and could not be
 * bothered. So the address is editable here, next to the button that uses it.
 *
 * "Never sent" is called out rather than shown as a zero. It is a different
 * problem from "sent and ignored" — one is P&O's to fix and the other is the
 * rater's — and chasing the wrong one wastes the week before a cycle closes.
 */
const relativeDate = (iso: string, locale: string): string =>
  new Intl.DateTimeFormat(locale === 'en' ? 'en-GB' : 'es-ES', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));

export const InvitationCard = ({ personId, detail, onChange }: Props) => {
  const { t, locale } = useI18n();
  /**
   * A coach sees the address and no controls. Chasing participation is P&O's
   * job, and a coach editing the address of somebody they are about to assess
   * blurs a line worth keeping. The API refuses them either way; this stops the
   * screen offering something that cannot happen.
   */
  const canInvite = useCan('rater:invite');
  // Issuing an account is a different authority from sending a survey link.
  const canGrantAccess = useCan('staff:manage');
  const [access, setAccess] = useState<string | null>(null);

  const [email, setEmail] = useState(detail.email);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<string | null>(null);
  /** Set when the address moved and the old links died with it. */
  const [reinvite, setReinvite] = useState<number | null>(null);

  const placeholder = detail.emailIsPlaceholder;

  const save = async () => {
    setBusy('email');
    setError(null);
    try {
      const saved = await updatePersonEmail(personId, email);
      // The counters really did go back to zero on the server; showing the old
      // ones would have the card contradict the list on the next screen.
      onChange({
        email: saved.email,
        emailIsPlaceholder: false,
        ...(saved.needsReinvite
          ? {
              invitations: detail.invitations.map((entry) => ({
                ...entry,
                timesSent: 0,
                lastSentAt: null,
              })),
            }
          : {}),
      });
      setEmail(saved.email);
      setEditing(false);
      setReinvite(saved.needsReinvite ? saved.revokedLinks : null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t('errors.generic'));
    } finally {
      setBusy(null);
    }
  };

  const saveLocale = async (value: string) => {
    const next = value === '' ? null : (value as Locale);
    setBusy('locale');
    setError(null);
    try {
      await updatePersonLocale(personId, next);
      onChange({ locale: next });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t('errors.generic'));
    } finally {
      setBusy(null);
    }
  };

  const send = async (invitation: InvitationDto) => {
    setBusy(invitation.surveyId);
    setError(null);
    setSent(null);
    try {
      await sendInvitation(personId, invitation.surveyId);
      // Reflect the send without a round trip: the card has to show the new
      // count immediately or somebody clicks twice.
      onChange({
        invitations: detail.invitations.map((entry) =>
          entry.surveyId === invitation.surveyId
            ? {
                ...entry,
                timesSent: entry.timesSent + 1,
                lastSentAt: new Date().toISOString(),
              }
            : entry,
        ),
      });
      setSent(invitation.surveyId);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t('errors.generic'));
    } finally {
      setBusy(null);
    }
  };

  const grantAccess = async () => {
    setBusy('access');
    setError(null);
    try {
      const result = await sendParticipantAccess(personId);
      setAccess(result.invitedAt);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t('errors.generic'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="mt-6">
      <h2 className="mb-2.5 flex items-center gap-2 font-mono text-[10px] tracking-[0.13em] text-faint uppercase">
        {t('invitations.title')}
        <span className="h-px flex-1 bg-line-soft" />
      </h2>

      <div className="rounded-2xl border border-line bg-paper px-4 py-4 shadow-card">
        {/* --------------------------------------------------------- address */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="font-mono text-[10px] tracking-[0.12em] text-faint uppercase">
            {t('invitations.email')}
          </span>
          {!editing && canInvite && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="cursor-pointer border-none bg-transparent p-0 font-mono text-[10px] tracking-[0.08em] text-teal uppercase hover:text-teal-deep"
            >
              {t('common.edit')}
            </button>
          )}
        </div>

        {editing ? (
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            <input
              type="email"
              value={email}
              autoFocus
              onChange={(event) => setEmail(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') void save();
                if (event.key === 'Escape') {
                  setEmail(detail.email);
                  setEditing(false);
                }
              }}
              className="min-w-[240px] flex-1 rounded-xl border border-line bg-sand px-3 py-2 text-[14px] outline-none focus:border-teal"
            />
            <button
              type="button"
              disabled={busy === 'email' || email.trim() === ''}
              onClick={() => void save()}
              className="cursor-pointer rounded-xl bg-teal px-3.5 py-2 text-[13px] font-bold text-white shadow-action transition hover:bg-teal-deep disabled:cursor-default disabled:opacity-40"
            >
              {busy === 'email' ? t('common.saving') : t('common.save')}
            </button>
            <button
              type="button"
              onClick={() => {
                setEmail(detail.email);
                setEditing(false);
                setError(null);
              }}
              className="cursor-pointer border-none bg-transparent px-1 py-2 font-mono text-[11px] tracking-[0.06em] text-soft uppercase"
            >
              {t('common.cancel')}
            </button>
          </div>
        ) : (
          <p className="mt-1 text-[15px] break-all">
            {placeholder ? (
              <span className="text-faint italic">{t('invitations.noEmail')}</span>
            ) : (
              detail.email
            )}
          </p>
        )}

        {/* --------------------------------------------------------- language */}
        <div className="mt-3.5 flex flex-wrap items-center justify-between gap-2 border-t border-line-soft pt-3">
          <span>
            <span className="block font-mono text-[10px] tracking-[0.12em] text-faint uppercase">
              {t('invitations.language')}
            </span>
            <span className="mt-0.5 block text-[12.5px] text-faint">
              {t('invitations.languageHelp')}
            </span>
          </span>

          {canInvite ? (
            <select
              value={detail.locale ?? ''}
              disabled={busy === 'locale'}
              onChange={(event) => void saveLocale(event.target.value)}
              className="cursor-pointer rounded-xl border border-line bg-paper px-3 py-2 text-[13.5px] outline-none focus:border-teal disabled:opacity-50"
            >
              <option value="">{t('invitations.languageDefault')}</option>
              {LOCALES.map((option) => (
                <option key={option} value={option}>
                  {LOCALE_LABELS[option]}
                </option>
              ))}
            </select>
          ) : (
            <span className="text-[14px]">
              {detail.locale ? LOCALE_LABELS[detail.locale] : t('invitations.languageDefault')}
            </span>
          )}
        </div>

        {/* Correcting an address is half the job: the link that went to the
            old one is dead, and nothing has reached the new one yet. Left
            unsaid, somebody fixes a typo and waits for an answer that was
            never going to come. */}
        {reinvite !== null && !editing && (
          <p className="mt-1.5 rounded-xl border border-tint-line bg-tint px-3 py-2 text-[12.5px] text-teal-deep">
            {t('invitations.addressMovedNeedsResend', { revoked: reinvite })}
          </p>
        )}

        {placeholder && !editing && (
          <p className="mt-1.5 text-[12.5px] text-[#8A6A1F]">
            {canInvite ? t('invitations.noEmailHelp') : t('invitations.noEmailReadOnly')}
          </p>
        )}

        {/* --------------------------------------------- reading their report

            A different thing from the questionnaire links below, and easy to
            confuse with them: those send a magic link to answer with, this
            hands over an account to read the finished report with. Kept apart,
            and labelled as what the leader gets rather than as what is sent. */}
        {canGrantAccess && !placeholder && (
          <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-line-soft pt-3">
            <span className="min-w-[180px] flex-1">
              <span className="block font-mono text-[10px] tracking-[0.12em] text-faint uppercase">
                {t('invitations.reportAccess')}
              </span>
              <span className="mt-0.5 block text-[12.5px] text-soft">
                {access ? t('invitations.reportAccessSent') : t('invitations.reportAccessHelp')}
              </span>
            </span>
            <button
              type="button"
              disabled={busy === 'access'}
              onClick={() => void grantAccess()}
              className="cursor-pointer rounded-xl border border-line bg-paper px-3.5 py-2 text-[13px] font-semibold transition hover:border-teal hover:text-teal disabled:opacity-40"
            >
              {busy === 'access' ? t('common.saving') : t('invitations.sendReportAccess')}
            </button>
          </div>
        )}

        {/* ----------------------------------------------------- the surveys */}
        <div className="mt-4 border-t border-line-soft pt-3">
          {detail.invitations.length === 0 && (
            <p className="text-[13px] text-faint">{t('invitations.none')}</p>
          )}

          {detail.invitations.map((invitation) => {
            const done = invitation.status === 'SUBMITTED';
            return (
              <div
                key={invitation.surveyId}
                className="flex flex-wrap items-center justify-between gap-3 border-t border-line-soft py-2.5 first:border-t-0 first:pt-0"
              >
                <span>
                  <span className="block text-[14px] font-semibold">
                    {invitation.kind === 'SELF'
                      ? t('invitations.selfSurvey')
                      : t('invitations.othersSurvey', { count: invitation.evaluatedCount })}
                  </span>
                  <span className="mt-0.5 block text-[12.5px] text-soft">
                    {done
                      ? t('invitations.completed')
                      : invitation.timesSent === 0
                        ? // Never told is not the same as told and ignored, and
                          // only one of the two is P&O's to fix.
                          t('invitations.neverSent')
                        : invitation.lastSentAt
                          ? t('invitations.sentOn', {
                              times: invitation.timesSent,
                              when: relativeDate(invitation.lastSentAt, locale),
                            })
                          : t('invitations.sentTimes', { times: invitation.timesSent })}
                  </span>
                </span>

                {!done && canInvite && (
                  <button
                    type="button"
                    disabled={placeholder || busy !== null}
                    title={placeholder ? t('invitations.noEmailHelp') : undefined}
                    onClick={() => void send(invitation)}
                    className={[
                      'shrink-0 cursor-pointer rounded-xl px-3.5 py-2 text-[13px] font-bold transition disabled:cursor-default disabled:opacity-40',
                      invitation.timesSent === 0
                        ? 'bg-teal text-white shadow-action hover:bg-teal-deep'
                        : 'border border-line bg-paper hover:border-teal',
                    ].join(' ')}
                  >
                    {busy === invitation.surveyId
                      ? t('invitations.sending')
                      : invitation.timesSent === 0
                        ? t('invitations.send')
                        : t('invitations.resend')}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {sent && <p className="mt-2.5 text-[13px] text-teal-deep">{t('invitations.sentOk')}</p>}
        {error && <p className="mt-2.5 text-[13px] text-red-700">{error}</p>}
      </div>
    </section>
  );
};
